import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import './Dashboard.css';

export const Dashboard = () => {
  const { user, isAdmin, getAccessToken } = useAuth();
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedScript, setSelectedScript] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);
  const [scriptParams, setScriptParams] = useState({});
  const hasLoadedScripts = useRef(false);
  
  // Admin-specific state
  const [targetEmail, setTargetEmail] = useState('');
  const [targetEnv, setTargetEnv] = useState('prod');

  useEffect(() => {
    if (user && !hasLoadedScripts.current) {
      hasLoadedScripts.current = true;
      loadScripts();
    }
  }, [user]);

  const loadScripts = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessToken();
      apiClient.setAuthToken(token);
      const data = await apiClient.getScripts();
      setScripts(data.scripts || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteScript = async (e) => {
    e.preventDefault();
    
    if (!selectedScript) return;

    try {
      setExecuting(true);
      setExecutionResult(null);
      setError(null);

      const token = await getAccessToken();
      apiClient.setAuthToken(token);

      const result = await apiClient.executeScript({
        scriptId: selectedScript.scriptId,
        targetEmail: isAdmin && targetEmail ? targetEmail : undefined,
        targetEnv: isAdmin ? targetEnv : 'prod',
        params: scriptParams
      });

      setExecutionResult(result);
      setScriptParams({});
      setSelectedScript(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setExecuting(false);
    }
  };

  const handleParamChange = (paramName, value) => {
    setScriptParams(prev => ({
      ...prev,
      [paramName]: value
    }));
  };

  if (loading) {
    return <div className="loading">Loading scripts...</div>;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Available Scripts</h2>
        <button onClick={loadScripts} className="btn btn-secondary">
          Refresh
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {executionResult && (
        <div className="alert alert-success">
          <h3>Execution Result</h3>
          <p><strong>Run ID:</strong> {executionResult.runId}</p>
          <p><strong>Status:</strong> {executionResult.status}</p>
          <pre>{JSON.stringify(executionResult.result, null, 2)}</pre>
          <button 
            onClick={() => setExecutionResult(null)} 
            className="btn btn-secondary"
          >
            Close
          </button>
        </div>
      )}

      <div className="scripts-grid">
        {scripts.length === 0 ? (
          <div className="empty-state">
            <p>No scripts available. Contact an administrator to enable scripts for your account.</p>
          </div>
        ) : (
          scripts.map(script => (
            <div key={script.scriptId} className="script-card">
              <div className="script-header">
                <h3>{script.displayName}</h3>
                <span className={`risk-badge risk-${script.riskTier}`}>
                  {script.riskTier}
                </span>
              </div>
              <p className="script-description">{script.description}</p>
              <div className="script-meta">
                <span className="env-badges">
                  {script.allowedEnvs.map(env => (
                    <span key={env} className="env-badge">{env}</span>
                  ))}
                </span>
              </div>
              <button
                onClick={() => setSelectedScript(script)}
                className="btn btn-primary"
                disabled={executing}
              >
                Execute
              </button>
            </div>
          ))
        )}
      </div>

      {selectedScript && (
        <div className="modal-overlay" onClick={() => setSelectedScript(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Execute: {selectedScript.displayName}</h2>
              <button 
                onClick={() => setSelectedScript(null)} 
                className="close-btn"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleExecuteScript}>
              {isAdmin && (
                <div className="admin-controls">
                  <div className="form-group">
                    <label>Target Email (optional)</label>
                    <input
                      type="email"
                      value={targetEmail}
                      onChange={(e) => setTargetEmail(e.target.value)}
                      placeholder={user.email}
                      className="form-control"
                    />
                    <small>Leave empty to execute for yourself</small>
                  </div>

                  <div className="form-group">
                    <label>Target Environment</label>
                    <select
                      value={targetEnv}
                      onChange={(e) => setTargetEnv(e.target.value)}
                      className="form-control"
                    >
                      {selectedScript.allowedEnvs.map(env => (
                        <option key={env} value={env}>{env}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {selectedScript.paramSchema && (
                <div className="params-section">
                  <h3>Parameters</h3>
                  {Object.entries(selectedScript.paramSchema.properties || {}).map(([paramName, paramDef]) => (
                    <div key={paramName} className="form-group">
                      <label>
                        {paramName}
                        {selectedScript.paramSchema.required?.includes(paramName) && (
                          <span className="required">*</span>
                        )}
                      </label>
                      {paramDef.type === 'integer' || paramDef.type === 'number' ? (
                        <input
                          type="number"
                          value={scriptParams[paramName] || ''}
                          onChange={(e) => handleParamChange(paramName, parseInt(e.target.value))}
                          min={paramDef.minimum}
                          max={paramDef.maximum}
                          required={selectedScript.paramSchema.required?.includes(paramName)}
                          className="form-control"
                        />
                      ) : (
                        <input
                          type="text"
                          value={scriptParams[paramName] || ''}
                          onChange={(e) => handleParamChange(paramName, e.target.value)}
                          required={selectedScript.paramSchema.required?.includes(paramName)}
                          className="form-control"
                        />
                      )}
                      {paramDef.description && (
                        <small>{paramDef.description}</small>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setSelectedScript(null)}
                  className="btn btn-secondary"
                  disabled={executing}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={executing}
                >
                  {executing ? 'Executing...' : 'Execute Script'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
